import { db } from '../lib/firebase';
import {
  collection,
  addDoc,
  setDoc,
  doc,
  updateDoc,
  getDoc,
  query,
  where,
  getDocs,
  onSnapshot,
  orderBy,
  serverTimestamp,
  limit,           
  runTransaction,
} from '../lib/firestoreClient';
import { errorDev, warnDev } from '../utils/logger';
import { ratingsRepository, DEFAULT_RATING_TARGET_TYPE } from '../repositories/ratingsRepository';
import { PRODUCT_SOLD_STATUS } from '../constants/farmConstants';

const OFFERS = 'offers';

/**
 * Create an offer document and return the saved doc (with server timestamps when possible).
 */
export const createOffer = async ({ productId, buyerId, sellerId, amount, message = '', chatId = null, metadata = {} }) => {
  const payload = {
    productId,
    buyerId,
    sellerId,
    amount,
    message,
    chatId: chatId || null,
    metadata,
    status: 'pending',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  const ref = await addDoc(collection(db, OFFERS), payload);

  // Try to read back so createdAt/updatedAt are actual Timestamps when possible.
  try {
    const snap = await getDoc(ref);
    return snap.exists() ? { id: snap.id, ...snap.data() } : { id: ref.id, ...payload };
  } catch (err) {
    warnDev('createOffer: failed to read back doc', err);
    return { id: ref.id, ...payload };
  }
};

/**
 * Read a single offer by id.
 */
export const getOfferById = async (id) => {
  if (!id) return null;
  const ref = doc(db, OFFERS, id);
  const snap = await getDoc(ref);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

/**
 * Update offer status. Caller must ensure the user has permission per Firestore rules.
 */
export const updateOfferStatus = async (id, status, actorId = null) => {
  const ref = doc(db, OFFERS, id);
  await updateDoc(ref, { status, updatedAt: serverTimestamp(), lastActionBy: actorId });
  return true;
};

/**
 * Real-time listener merging offers where user is seller OR buyer.
 * Returns an unsubscribe function.
 *
 * Strategy:
 * - Try the "indexed" queries (where + orderBy createdAt desc). If Firestore throws 'failed-precondition'
 *   (index required), re-register a fallback listener without orderBy and do client-side sorting.
 */
export const listenOffersForUser = (uid, cb) => {
  if (!uid) {
    // no-op unsubscribe
    return () => {};
  }

  const map = new Map();

  // Try to construct "preferred" queries (these may require composite indexes)
  let qSellerIndexed = null;
  let qBuyerIndexed = null;
  try {
    qSellerIndexed = query(collection(db, OFFERS), where('sellerId', '==', uid), orderBy('createdAt', 'desc'));
    qBuyerIndexed = query(collection(db, OFFERS), where('buyerId', '==', uid), orderBy('createdAt', 'desc'));
  } catch (err) {
    qSellerIndexed = null;
    qBuyerIndexed = null;
  }

  const sortByCreatedAtDesc = (arr) => {
    return arr.sort((a, b) => {
      const aTs = a.createdAt?.seconds ?? a.createdAt ?? 0;
      const bTs = b.createdAt?.seconds ?? b.createdAt ?? 0;
      return bTs - aTs;
    });
  };

  let unsubSeller = null;
  let unsubBuyer = null;

  const handleSnapshot = (snap) => {
    snap.docs.forEach((d) => {
      map.set(d.id, { id: d.id, ...d.data() });
    });
    const arr = sortByCreatedAtDesc(Array.from(map.values()));
    try { cb(arr); } catch (e) { errorDev('listenOffersForUser: callback error', e); }
  };

  const attachFallback = (which) => {
    const field = which === 'seller' ? 'sellerId' : 'buyerId';
    const qFallback = query(collection(db, OFFERS), where(field, '==', uid));
    const unsub = onSnapshot(qFallback, (snap) => {
      handleSnapshot(snap);
    }, (err) => {
      errorDev(`offers fallback (${which}) snapshot error`, err);
    });
    if (which === 'seller') {
      if (unsubSeller) unsubSeller();
      unsubSeller = unsub;
    } else {
      if (unsubBuyer) unsubBuyer();
      unsubBuyer = unsub;
    }
  };

  const handleError = (err, which) => {
    warnDev(`offers listener (${which}) error`, err);
    // Firestore uses 'failed-precondition' for missing composite index
    if (err && err.code === 'failed-precondition') {
      try {
        if (which === 'seller' && unsubSeller) unsubSeller();
        if (which === 'buyer' && unsubBuyer) unsubBuyer();
      } catch (_) {}
      if (which === 'seller') unsubSeller = null;
      else unsubBuyer = null;
      attachFallback(which);
    } else {
      errorDev('offers listener unexpected error', err);
    }
  };

  try {
    if (qSellerIndexed) {
      unsubSeller = onSnapshot(qSellerIndexed, handleSnapshot, (err) => handleError(err, 'seller'));
    } else {
      attachFallback('seller');
    }

    if (qBuyerIndexed) {
      unsubBuyer = onSnapshot(qBuyerIndexed, handleSnapshot, (err) => handleError(err, 'buyer'));
    } else {
      attachFallback('buyer');
    }
  } catch (err) {
    warnDev('listenOffersForUser: error attaching listeners, falling back', err);
    attachFallback('seller');
    attachFallback('buyer');
  }

  return () => {
    try { if (unsubSeller) unsubSeller(); } catch (_) {}
    try { if (unsubBuyer) unsubBuyer(); } catch (_) {}
  };
};

/**
 * Convenience: fetch offers for a given seller (one-time)
 */
export const getOffersForSeller = async (sellerId) => {
  const q = query(collection(db, OFFERS), where('sellerId', '==', sellerId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

/**
 * Update the in-chat message's extra.offerStatus for a given offerId.
 * Looks up the offer (for chatId) and updates matching message(s) in that chat.
 * Safe to call even if the message isn't found (no-ops).
 */
export const updateOfferMessageStatus = async (offerId, status) => {
  if (!offerId) return false;

  const offer = await getOfferById(offerId);
  if (!offer || !offer.chatId) return false;

  const messagesRef = collection(db, "chats", offer.chatId, "messages");
  const q = query(messagesRef, where("extra.offerId", "==", offerId), limit(5));
  const snap = await getDocs(q);

  const tasks = snap.docs.map((d) =>
    updateDoc(d.ref, { "extra.offerStatus": status, updatedAt: serverTimestamp() }).catch(() => {})
  );
  await Promise.all(tasks);
  return true;
};

/**
 * Deprecated helper kept for backward compatibility.
 * Offer payment/delivery progression must go through confirmOfferDeliveryStepInChat,
 * because current security rules do not allow direct paymentStatus writes on offers.
 */
export const markPaymentComplete = async (offerId) => {
  if (!offerId) return false;
  throw new Error('markPaymentComplete is deprecated. Use confirmOfferDeliveryStepInChat instead.');
};

/**
 * Confirm delivery lifecycle for accepted offers.
 * action: 'sent' (seller) | 'received' (buyer)
 * Product is marked sold only after both confirmations are complete.
 */
export const confirmOfferDeliveryStep = async ({ offerId, action, actorId }) => {
  if (!offerId) throw new Error('offerId is required');
  if (!actorId) throw new Error('actorId is required');
  if (action !== 'sent' && action !== 'received') {
    throw new Error('Invalid action');
  }

  const offerRef = doc(db, OFFERS, offerId);

  const result = await runTransaction(db, async (tx) => {
    const offerSnap = await tx.get(offerRef);
    if (!offerSnap.exists()) throw new Error('Offer not found');

    const offer = { id: offerSnap.id, ...offerSnap.data() };
    const isSeller = String(actorId) === String(offer.sellerId);
    const isBuyer = String(actorId) === String(offer.buyerId);

    if (action === 'sent' && !isSeller) {
      throw new Error('Only seller can confirm sent');
    }
    if (action === 'received' && !isBuyer) {
      throw new Error('Only buyer can confirm received');
    }

    const updates = {
      dealStatus: offer.dealStatus || 'accepted',
      updatedAt: serverTimestamp(),
    };

    if (action === 'sent') {
      updates.deliverySentAt = serverTimestamp();
      updates.deliverySentBy = actorId;
    }

    if (action === 'received') {
      const sentAlready = !!offer.deliverySentAt;
      if (!sentAlready) {
        throw new Error('Seller must confirm sent first');
      }
      updates.deliveryReceivedAt = serverTimestamp();
      updates.deliveryReceivedBy = actorId;
    }

    const sentDone = action === 'sent' ? true : !!offer.deliverySentAt;
    const receivedDone = action === 'received' ? true : !!offer.deliveryReceivedAt;
    const completed = sentDone && receivedDone;

    if (completed) {
      updates.dealStatus = 'completed';
      updates.completedAt = serverTimestamp();
    }

    tx.update(offerRef, updates);

    return {
      ...offer,
      deliverySentAt: sentDone ? (offer.deliverySentAt || true) : null,
      deliveryReceivedAt: receivedDone ? (offer.deliveryReceivedAt || true) : null,
      dealStatus: completed ? 'completed' : (offer.dealStatus || 'accepted'),
      completed,
    };
  });
};

/**
 * Rules-compliant delivery confirmation using chat metadata.
 * Both participants can update chat docs in current security rules.
 */
export const confirmOfferDeliveryStepInChat = async ({
  offerId,
  chatId,
  action,
  actorId,
}) => {
  if (!offerId) throw new Error('offerId is required');
  if (!chatId) throw new Error('chatId is required');
  if (!actorId) throw new Error('actorId is required');
  if (action !== 'sent' && action !== 'received') throw new Error('Invalid action');

  const offerRef = doc(db, OFFERS, offerId);
  const chatRef = doc(db, 'chats', String(chatId));

  return runTransaction(db, async (tx) => {
    const [offerSnap, chatSnap] = await Promise.all([tx.get(offerRef), tx.get(chatRef)]);
    if (!offerSnap.exists()) throw new Error('Offer not found');
    if (!chatSnap.exists()) throw new Error('Chat not found');

    const offer = { id: offerSnap.id, ...offerSnap.data() };
    const chat = { id: chatSnap.id, ...chatSnap.data() };
    const isSeller = String(actorId) === String(offer.sellerId);
    const isBuyer = String(actorId) === String(offer.buyerId);

    if (action === 'sent' && !isSeller) throw new Error('Only seller can confirm sent');
    if (action === 'received' && !isBuyer) throw new Error('Only buyer can confirm received');

    const pathBase = `dealConfirmations.${offerId}`;
    const sentAt = chat?.dealConfirmations?.[offerId]?.sentAt || null;
    const receivedAt = chat?.dealConfirmations?.[offerId]?.receivedAt || null;

    // Avoid redundant writes when state is already confirmed.
    if (action === 'sent' && sentAt) {
      return {
        changed: false,
        completed: !!receivedAt,
        isSeller,
        sent: true,
        received: !!receivedAt,
        dealStatus: receivedAt ? 'completed' : (chat?.dealConfirmations?.[offerId]?.status || 'accepted'),
        productId: offer.productId || null,
        action,
      };
    }
    if (action === 'received' && receivedAt) {
      return {
        changed: false,
        completed: !!sentAt,
        isSeller,
        sent: !!sentAt,
        received: true,
        dealStatus: sentAt ? 'completed' : (chat?.dealConfirmations?.[offerId]?.status || 'accepted'),
        productId: offer.productId || null,
        action,
      };
    }

    if (action === 'received' && !sentAt) {
      throw new Error('Seller must confirm sent first');
    }

    const updates = { updatedAt: serverTimestamp() };
    if (action === 'sent') {
      updates[`${pathBase}.sentAt`] = serverTimestamp();
      updates[`${pathBase}.sentBy`] = actorId;
    }
    if (action === 'received') {
      updates[`${pathBase}.receivedAt`] = serverTimestamp();
      updates[`${pathBase}.receivedBy`] = actorId;
    }

    const nextSent = action === 'sent' ? true : !!sentAt;
    const nextReceived = action === 'received' ? true : !!receivedAt;
    const completed = nextSent && nextReceived;
    updates[`${pathBase}.status`] = completed ? 'completed' : 'accepted';

    tx.update(chatRef, updates);

    return {
      changed: true,
      completed,
      isSeller,
      sent: nextSent,
      received: nextReceived,
      dealStatus: completed ? 'completed' : 'accepted',
      productId: offer.productId || null,
      action,
    };
  });
};

/**
 * Submit a rating after deal completion.
 * Each star equals one IP point and is added to the rated user.
 */
export const submitOfferRating = async ({
  offerId,
  fromUserId,
  chatId = null,
  stars,
  comment = '',
}) => {
  if (!offerId) throw new Error('offerId is required');
  if (!fromUserId) throw new Error('fromUserId is required');

  const ratingValue = Number(stars);
  if (!Number.isFinite(ratingValue) || ratingValue < 1 || ratingValue > 5) {
    throw new Error('stars must be between 1 and 5');
  }

  const offerRef = doc(db, OFFERS, offerId);
  const chatRef = chatId ? doc(db, 'chats', String(chatId)) : null;

  const [offerSnap, chatSnap] = await Promise.all([
    getDoc(offerRef),
    chatRef ? getDoc(chatRef).catch(() => null) : Promise.resolve(null),
  ]);

  if (!offerSnap.exists()) throw new Error('Offer not found');

  const offer = { id: offerSnap.id, ...offerSnap.data() };
  const isSeller = String(fromUserId) === String(offer.sellerId);
  const isBuyer = String(fromUserId) === String(offer.buyerId);
  if (!isSeller && !isBuyer) {
    throw new Error('Only buyer or seller can rate this deal');
  }

  const dealStatus = String(offer.dealStatus || '').toLowerCase();
  const confirmation = chatSnap?.exists() ? (chatSnap.data()?.dealConfirmations?.[offerId] || null) : null;
  const completedFromChat = String(confirmation?.status || '').toLowerCase() === 'completed';
  const sentFromChat = Boolean(confirmation?.sentAt);
  const receivedFromChat = Boolean(confirmation?.receivedAt);

  const canSellerRateAfterSent = isSeller && sentFromChat;
  const canBuyerRateAfterReceived = isBuyer && (receivedFromChat || completedFromChat || dealStatus === 'completed');

  if (dealStatus !== 'completed' && !completedFromChat && !canSellerRateAfterSent && !canBuyerRateAfterReceived) {
    throw new Error('Deal must be completed before rating');
  }

  const toUserId = isSeller ? String(offer.buyerId) : String(offer.sellerId);
  const ratingId = `offer_${offerId}_${fromUserId}_${toUserId}`;
  const ratingRef = doc(db, 'ratings', ratingId);
  const reciprocalRatingId = `offer_${offerId}_${toUserId}_${fromUserId}`;
  const reciprocalRatingRef = doc(db, 'ratings', reciprocalRatingId);

  const existing = await getDoc(ratingRef);
  if (existing.exists()) {
    throw new Error('You already rated this user for this deal');
  }

  const normalizedComment = String(comment || '').trim();
  const ipPoints = ratingValue;

  // Primary write: fast and rules-safe so rating succeeds reliably. The document
  // body comes from the one canonical builder, so the writer cannot drift from
  // `ratingsRepository` readers or from the `firestore.rules` create contract.
  await setDoc(
    ratingRef,
    ratingsRepository.buildDocument({
      raterId: fromUserId,
      targetId: toUserId,
      targetType: DEFAULT_RATING_TARGET_TYPE,
      rating: ratingValue,
      comment: normalizedComment,
      extra: {
        fromUser: fromUserId,
        toUser: toUserId,
        transactionType: 'product',
        transactionId: offerId,
        offerId,
        productId: offer.productId || null,
        ipPoints,
        createdAt: serverTimestamp(),
      },
    })
  );

  const reciprocal = await getDoc(reciprocalRatingRef).catch(() => null);
  const bothRated = Boolean(reciprocal && reciprocal.exists());

  if (bothRated && offer.productId) {
    try {
      await updateDoc(doc(db, 'products', String(offer.productId)), {
        sold: true,
        status: PRODUCT_SOLD_STATUS,
        soldAt: serverTimestamp(),
        soldToUserId: String(offer.buyerId || ''),
        soldOfferId: String(offerId),
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      // Non-owner writes are intentionally non-blocking; backend trigger finalizes this.
    errorDev("Operation failed:", err);
    }
  }

  // Best-effort side effects; never fail rating submission.
  try {
    await addDoc(collection(db, 'impact_points_events'), {
      userId: toUserId,
      actorId: fromUserId,
      action: 'product_rating_received',
      category: 'topGrowers',
      points: ipPoints,
      metadata: {
        offerId,
        productId: offer.productId || null,
        ratingId,
      },
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    // Safe to skip if user lacks create rights for this collection in client rules.
  errorDev("Operation failed:", err);
  }

  return {
    ok: true,
    toUserId,
    ipPoints,
    ratingId,
    bothRated,
  };
};